import { IColumn, IDetailsListStyles, mergeStyleSets, Modal, SelectionMode } from '@fluentui/react';
import { ReactNode } from 'react';

import { SafeExternalLink } from '@shared/lib/ui/components-alpha/SafeExternalLink';

import logo from '../../../assets/images/logo.svg';
import modalBanner from '../../../assets/images/modalBanner.png';
import { Button } from '../../../components/Button/Button';
import { ClipboardButton } from '../../../components/ClipboardButton/ClipboardButton';
import { DetailsList } from '../../../components/DetailsList/DetailsList';
import {
  detailsHeaderStylesBasic,
  detailsListStylesBasic,
  detailsRowStylesBasic,
} from '../../../components/DetailsList/DetailsList.styles';
import { EmptyMessage } from '../../../components/EmptyMessage/EmptyMessage';
import { Image } from '../../../components/Image/Image';
import { IH2OTheme } from '../../../models/components.models';
import { FontSizes, FontWeights, Sizes } from '../../../themes/styles';
import { useTheme } from '../../../utils/hooks';

export type ComponentVersion = {
  displayName: string;
  version: string;
};

const contentStyles = (theme: IH2OTheme) =>
  mergeStyleSets({
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      minHeight: 100,
      width: '35%',
      position: 'fixed',
      borderRadius: Sizes.borderRadius,
      '@media(max-width: 1780px) and (min-width: 1201px)': {
        width: '50%',
      },
      '@media(max-width: 1200px)': {
        width: '75%',
      },
    },
    header: [
      {
        display: 'flex',
        alignItems: 'baseline',
        flexFlow: 'row wrap',
        gap: 10,
        fontSize: FontSizes.large,
        color: theme.palette?.gray750,
        fontWeight: FontWeights.semiBold,
        padding: Sizes.modalHeaderPadding,
        backgroundPosition: 'top right',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#ffe500', // there is no provision for this color in the H2O theme, unfortunately.
        height: 175,
        lineHeight: 175,
      },
    ],
    title: { gap: 10, display: 'flex' },
    body: {
      padding: Sizes.modalHeaderPadding,
    },
    button: {
      marginLeft: 'auto',
    },
    licenseLink: {
      fontSize: FontSizes.xsmall,
    },
    clipboard: {
      position: 'absolute',
      right: Sizes.modalHeaderPadding,
      top: Sizes.modalHeaderPadding,
      lineHeight: Sizes.modalHeaderPadding,
    },
    cloudVersion: {
      fontSize: FontSizes.large,
      fontWeight: FontWeights.normal,
      marginRight: 'auto',
    },
    footer: {
      lineHeight: Sizes.modalHeaderPadding,
      padding: `0 ${Sizes.modalHeaderPadding}px ${Sizes.modalHeaderPadding}px ${Sizes.modalHeaderPadding}px`,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
  });

export const detailsListStyles = (theme: IH2OTheme): Partial<IDetailsListStyles> => ({
  contentWrapper: {
    border: `solid 1px ${theme.palette?.gray300}`,
  },
  focusZone: {
    paddingTop: 0,
  },
});

export interface AboutDialogProps {
  licenceUrl?: string;
  headerBackgroundImageUrl?: string;
  logoUrl?: string; // The deprecated prop
  logoElement?: ReactNode; // The replacement prop
  hidden?: boolean;
  onDismiss?: () => void;
  services: ComponentVersion[];
  cloudVersion: string;
  versionInfo: string;
}

enum fieldProps {
  DISPLAYNAME = 'displayName',
  VERSION = 'version',
}

export function AboutDialog(props: AboutDialogProps) {
  const {
      hidden,
      licenceUrl,
      onDismiss,
      services = [],
      logoUrl = logo,
      logoElement,
      headerBackgroundImageUrl = modalBanner,
      cloudVersion = '',
      versionInfo = '',
    } = props,
    theme = useTheme(),
    modalClassNames = contentStyles(theme),
    onRenderItemColumn = (item: ComponentVersion, _index?: number, column?: IColumn) => {
      if (!column) return <span />;
      const fieldContent = item[column.fieldName as keyof ComponentVersion];
      if (!fieldContent) return <span />;
      return <span>{fieldContent}</span>;
    };

  return (
    <Modal isOpen={!hidden} isBlocking={false} containerClassName={modalClassNames.container} onDismiss={onDismiss}>
      <div
        className={modalClassNames.header}
        style={{
          backgroundImage: `url(${headerBackgroundImageUrl})`,
        }}
      >
        <div className={modalClassNames.title}>
          <h2>H2O AI Cloud</h2>
        </div>
        <div className={modalClassNames.cloudVersion}>{cloudVersion}</div>
        <div className={modalClassNames.clipboard}>
          <ClipboardButton block content={versionInfo} styles={{ icon: { fontSize: FontSizes.xlarge } }} />
        </div>
      </div>
      {services.length > 0 && (
        <div className={modalClassNames.body}>
          <DetailsList
            selectionMode={SelectionMode.none}
            columns={[
              { fieldName: fieldProps.DISPLAYNAME, name: 'Components', key: fieldProps.DISPLAYNAME, minWidth: 100 },
              { fieldName: fieldProps.VERSION, name: 'Version(s)', key: fieldProps.VERSION, minWidth: 150 },
            ]}
            items={services}
            headerStyles={[
              detailsHeaderStylesBasic,
              {
                root: {
                  borderRight: `2px solid ${theme.palette?.gray200}`,
                  '.ms-DetailsHeader-cellTitle': {
                    paddingLeft: 12,
                  },
                  '.ms-DetailsHeader-cell': {
                    border: 'none',
                  },
                  fontSize: FontSizes.xsmall,
                },
              },
            ]}
            rowStyles={[
              detailsRowStylesBasic,
              {
                root: {
                  borderBottom: 'none',
                  fontSize: FontSizes.xsmall,
                },
              },
            ]}
            styles={[
              detailsListStylesBasic,
              {
                root: {
                  fontSize: FontSizes.xsmall,
                },
              },
            ]}
            onRenderItemColumn={onRenderItemColumn}
          />
        </div>
      )}
      {services.length === 0 && (
        <div className={modalClassNames.body}>
          <EmptyMessage message="No services found" />
        </div>
      )}
      <div className={modalClassNames.footer}>
        {logoElement ? (
          logoElement
        ) : (
          // Fallback for the logoUrl prop
          <Image
            src={logoUrl}
            styles={{
              root: {
                height: 20,
                width: 20,
              },
            }}
          />
        )}
        {`©${new Date().getFullYear()} H2O.ai, All Rights Reserved${licenceUrl ? ' | ' : ''}`}
        {licenceUrl && (
          <SafeExternalLink href={licenceUrl} className={modalClassNames.licenseLink}>
            Packages used in this product
          </SafeExternalLink>
        )}
        <Button className={modalClassNames.button} onClick={onDismiss}>
          Close
        </Button>
      </div>
    </Modal>
  );
}
